// src/pages/Profile.js
import React, { useState, useEffect } from "react";
import api, { setToken } from "../api";
import {
  ClipboardDocumentIcon,
  CheckIcon,
  BanknotesIcon,
  UserCircleIcon,
  UserGroupIcon,
  ChartBarSquareIcon,
  ArrowTrendingUpIcon
} from "@heroicons/react/24/solid";
import { useNavigate } from 'react-router-dom';
import Avatar from "../components/Avatar";

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(false);
  const [activeCard, setActiveCard] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) setToken(token);

    api.get("/profile")
      .then(({ data }) => {
        console.log("→ [Profile] got data:", data);
        setProfile(data);
      })
      .catch(err => console.error("Failed to load profile:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = link => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse" />
            <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin" />
          </div>
          <div className="mt-4 font-mono text-sm">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Loading Profile…
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="p-6 border border-red-500 rounded-lg bg-black bg-opacity-60">
          <div className="flex items-center mb-2">
            <span className="text-red-500 font-bold">Error</span>
          </div>
          <div className="text-sm text-gray-300">
            Unable to load profile data. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const {
    email,
    referralCode,
    chips = 0,
    chipsEarned = 0,
    winRate = 0,
    predictions = 0,
    referrals = [],
    recentActivity = [],
    createdAt
  } = profile;

  // Ensure referrals is always an array
  const referralsArray = Array.isArray(referrals) ? referrals : [];
  const referralCount = Array.isArray(referrals)
    ? referrals.length
    : typeof referrals === "number"
      ? referrals
      : 0;

  const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;

  const stats = [
    {
      label: "Chips Earned",
      value: chipsEarned,
      icon: BanknotesIcon,
      color: "from-pink-500 to-red-500",
      id: "chipsEarned"
    },
    {
      label: "Win Rate",
      value: `${winRate}%`,
      icon: ArrowTrendingUpIcon,
      color: "from-violet-500 to-pink-500",
      id: "winRate"
    },
    {
      label: "Predictions",
      value: predictions,
      icon: ChartBarSquareIcon,
      color: "from-indigo-500 to-violet-500",
      id: "predictions"
    }
  ];

  const formatMemberSince = date => {
    if (!date) return "2024";
    const y = new Date(date).getFullYear();
    return isNaN(y) ? "2024" : y;
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-12 px-4 text-white font-mono">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-8">
          Your Profile
        </h1>

        {/* Identity Panel */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6">
            {/* Avatar */}
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full opacity-75 blur-sm" />

              <div className="relative rounded-full p-1 bg-gray-900">
                <Avatar
                  seed={referralCode}
                  size={96}
                  className="object-cover"
                />
              </div>

              <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-gray-900" />
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="mb-1 text-xs text-gray-400 uppercase tracking-wider">Account</div>
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 mb-2">
                {email}
              </h2>
              <div className="flex flex-wrap justify-center md:justify-start gap-3 text-xs">
                <div className="px-2 py-1 bg-black bg-opacity-40 rounded border border-gray-700">
                  <span className="text-gray-300">Member Since</span>
                  <span className="ml-2 text-pink-400">{formatMemberSince(createdAt)}</span>
                </div>
                <div className="px-2 py-1 bg-black bg-opacity-40 rounded border border-gray-700">
                  <span className="text-gray-300">ID</span>
                  <span className="ml-2 text-pink-400">{referralCode}</span>
                </div>
              </div>
              
              {/* My Bets button - added for mobile view */}
              <div className="mt-4 md:hidden">
                <button 
                  onClick={() => navigate('/my-bets')}
                  className="w-full px-4 py-2 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <ChartBarSquareIcon className="h-5 w-5" />
                  <span>My Bets</span>
                </button>
              </div>
            </div>

            {/* Balance and My Bets Button Column */}
            <div className="flex flex-col items-center space-y-4">
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Balance</div>
                <div className="relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg opacity-70 blur" />
                  <div className="relative px-8 py-3 bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-lg flex flex-col items-center">
                    <BanknotesIcon className="h-6 w-6 text-pink-400 mb-1" />
                    <div className="font-bold text-2xl text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                      {chips.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* My Bets button - for desktop view */}
              <div className="hidden md:block">
                <button 
                  onClick={() => navigate('/my-bets')}
                  className="px-6 py-2 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium transition-colors flex items-center space-x-2"
                >
                  <ChartBarSquareIcon className="h-5 w-5" />
                  <span>My Bets</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {stats.map(stat => (
            <div
              key={stat.id}
              className="relative rounded-lg overflow-hidden transition-all duration-300 transform hover:scale-105"
              onMouseEnter={() => setActiveCard(stat.id)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-r ${stat.color} opacity-0 transition-opacity duration-300 ${
                  activeCard === stat.id ? "opacity-100 blur" : ""
                }`}
              />
              <div className="bg-gradient-to-br from-black to-gray-900 border border-gray-700 rounded-lg p-5 relative">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400 uppercase tracking-wider">{stat.label}</div>
                  <stat.icon className="h-5 w-5 text-pink-400" />
                </div>
                <div className="text-2xl font-bold mt-2 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                  {typeof stat.value === "number"
                    ? stat.value.toLocaleString()
                    : stat.value}
                </div>
                <div className="mt-3 h-1 w-full bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${stat.color}`}
                    style={{
                      width:
                        typeof stat.value === "string"
                          ? stat.value
                          : `${Math.min(100, stat.value)}%`
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="mb-6 p-6 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl">
          <div className="flex items-center mb-4">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Recent Activity
            </h3>
            <div className="ml-2 px-2 py-1 bg-black bg-opacity-40 rounded text-xs text-pink-400 border border-pink-500">
              Last 7 days
            </div>
          </div>
          {recentActivity.length > 0 ? (
            recentActivity.map((act, i) => (
              <div
                key={i}
                className="flex items-center py-3 border-b border-gray-800 last:border-0"
              >
                {/* Icon */}
                <div className="h-8 w-8 rounded-md bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center mr-4">
                  {act.type === 'purchase' ? (
                    <BanknotesIcon className="h-5 w-5 text-pink-400" />
                  ) : (
                    <ChartBarSquareIcon className="h-5 w-5 text-pink-400" />
                  )}
                </div>

                {/* Main text */}
                <div className="flex-1">
                  {act.type === 'purchase' ? (
                    <div className="text-sm">
                      <span className="text-pink-400 font-medium">
                        Purchased {Number(act.amount).toLocaleString()} chips
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm">
                      Predicted{' '}
                      <span className="text-pink-400 font-medium">
                        {act.asset} {act.prediction}
                      </span>
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    {new Date(act.timestamp).toLocaleDateString()}
                  </div>
                </div>

                {/* Amount badge */}
                <div
                  className={`text-sm font-medium ${
                    act.type === 'purchase'
                      ? 'text-green-400'
                      : act.result === 'win'
                        ? 'text-green-400'
                        : 'text-red-400'
                  }`}
                >
                  {act.type === 'purchase'
                    ? `+${Number(act.amount).toLocaleString()}`
                    : `${act.result === 'win' ? '+' : '-'}${act.amount} chips`}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              No recent activity
            </div>
          )}
        </div>

        {/* Referral Program */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
                Referral Program
              </h2>
              <div className="h-px flex-grow ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />
            </div>

            {/* Your Referral Link */}
            <div className="mb-6">
              <div className="text-xs text-gray-400 mb-2 uppercase">Your Referral Link</div>
              <div className="flex items-center border border-gray-700 bg-black bg-opacity-50 rounded-lg overflow-hidden">
                <input
                  type="text"
                  readOnly
                  value={referralLink}
                  className="flex-grow bg-transparent py-3 px-4 text-sm text-white outline-none"
                />
                <button
                  onClick={() => handleCopy(referralLink)}
                  className="px-4 py-3 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 transition-colors flex items-center"
                >
                  {copied ? (
                    <CheckIcon className="h-5 w-5 text-white" />
                  ) : (
                    <ClipboardDocumentIcon className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-400">
                Earn 500 chips for each new user who joins
              </div>
            </div>

            {/* Referrals List */}
            <div>
              <div className="flex items-center mb-3">
                <UserGroupIcon className="h-5 w-5 text-pink-500 mr-2" />
                <h3 className="text-white">
                  Your Referrals <span className="text-pink-400">({referralCount})</span>
                </h3>
              </div>
              {referralsArray.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {referralsArray.map((r, i) => (
                    <div
                      key={i}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 px-4 rounded-lg border border-gray-700 bg-black bg-opacity-40 hover:border-pink-500 transition-colors"
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center mr-3 border border-gray-700">
                          {r.email ? r.email.charAt(0).toUpperCase() : "?"}
                        </div>
                        <div className="overflow-hidden">
                          <div className="text-sm text-white truncate max-w-full">{r.email || "Unknown"}</div>
                          <div className="text-xs text-gray-400">
                            Joined {r.joinedAt ? new Date(r.joinedAt).toLocaleDateString() : "Recently"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center text-xs mt-2 sm:mt-0 ml-11 sm:ml-0">
                        <div className="px-2 py-1 rounded-md bg-gradient-to-r from-violet-500/20 to-pink-500/20 text-pink-400 border border-pink-500/30">
                          +{r.bonusAmount || 500} CHIPS
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-gray-700 rounded-lg p-6 bg-black bg-opacity-40 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 mb-3">
                    <UserGroupIcon className="h-6 w-6 text-pink-400" />
                  </div>
                  <p className="text-gray-300 mb-2">No referrals yet</p>
                  <p className="text-sm text-pink-400">Share your link to invite friends</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 flex justify-center">
          <button onClick={() => navigate('/')} className="px-8 py-3 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg flex items-center space-x-2 border border-gray-700 hover:border-pink-500 transition-colors">
            <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-violet-400">
              ENTER ARENA
            </span>
            <svg className="h-5 w-5 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    </main>
  );
}
